import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
// QuestionPreview.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import FlashCard from "@/features/questions/components/flash-card";
import type { questionsWithFileName } from "@/lib/types";

interface QuestionPreviewProps {
  questions: questionsWithFileName[];
  status: "pending" | "error" | "success";
}

export const QuestionPreview = ({
  questions,
  status,
}: QuestionPreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [direction, setDirection] = useState(0); // -1 for left, 1 for right

  const currentQuestion = questions[currentIndex];

  const flashcardProps = {
    flipped: false,
    currentIndex: currentIndex,
    front: {
      question: currentQuestion.questions.question,
      questionImageUrl: currentQuestion.questions.questionImageUrl,
    },
    back: {
      answer: currentQuestion.questions.answer,
      answerImageUrl: currentQuestion.questions.answerImageUrl,
    },
    isFlipped: isFlipped,
    toggleIsFlipped: () => setIsFlipped((prev) => !prev),
    isCorrect: false,
    activeIndex: currentIndex,
  };

  const handlePrevious = () => {
    setIsFlipped(false);
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + questions.length) % questions.length);
  };

  const handleNext = () => {
    setIsFlipped(false);
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % questions.length);
  };

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? "100%" : "-100%",
      opacity: 0,
    }),
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <AnimatePresence initial={false} mode="wait" custom={direction}>
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            type: "tween",
            duration: 0.3,
            ease: "easeInOut",
          }}
        >
          <FlashCard {...flashcardProps} />
        </motion.div>
      </AnimatePresence>
      <div className="flex items-center justify-center gap-6 w-full">
        <Button variant="outline" size={"icon"} onClick={handlePrevious}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-normal text-muted-foreground">
          {currentIndex + 1}/{questions.length}
        </span>
        <Button variant="outline" size={"icon"} onClick={handleNext}>
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
